// src/hooks/useInbounds.tsx
import { useCallback, useEffect, useState } from 'react';
import { dbAPI, STORE_NAMES } from '../utils/db';
import { apiGetInbounds } from '../services/inboundAPI';

const SEED_KEY = 'inbounds.synced.v1';

const CATEGORY_LABELS: Record<string, string> = {
  sparkPlugs:     'Свечи зажигания',
  ignitionCoils:  'Катушки зажигания',
  sensors:        'Датчики',
  steeringClocks: 'Рулевые шлейфы',
  wires:          'Наконечники и провода',
};

export type InboundLine = {
  sku?: string;
  name?: string;
  qty?: number;
  unitCost?: number | null;
  totalCost?: number | null;
  quality?: 'original' | 'copy' | 'Оригинал' | 'Копия' | string;
};

export type Inbound = {
  id: string;
  createdAt: string;
  supplier?: string;
  status?: 'ready' | 'processing' | 'in_transit' | string;
  lines?: InboundLine[];
  qty?: number;
  totalCost?: number | null;
};

export function useInbounds(limit = 10000) {
  const [items, setItems] = useState<Inbound[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch from MySQL backend, fall back to IndexedDB cache
      let rows: Inbound[] = [];
      try {
        const remote = await apiGetInbounds();
        rows = remote.map((r: any) => ({
          id:        r.id,
          seqNum:    r.seq_num,
          createdAt: r.created_at ?? r.createdAt,
          supplier:  r.supplier,
          status:    r.status,
          lines:     (r.lines ?? []).map((l: any) => ({
            sku:        l.sku,
            name:       CATEGORY_LABELS[l.category] || l.category || '—',
            brand:      l.brand,
            partNumber: l.part_number,
            model:      l.model,
            category:   l.category,
            qty:        l.qty,
            unitCost:   l.unit_cost,
            totalCost:  l.total_cost,
            quality:    l.quality,
          })),
          totalCost: (r.lines ?? []).reduce((s: number, l: any) => s + (l.total_cost || 0), 0),
        }));
        // Cache in IndexedDB
        await dbAPI.clear(STORE_NAMES.inbounds);
        for (const row of rows) await dbAPI.put(STORE_NAMES.inbounds, row);
        localStorage.setItem(SEED_KEY, '1');
      } catch {
        // Fallback to local cache
        rows = await dbAPI.getAll<Inbound>(STORE_NAMES.inbounds).catch(() => []);
      }

      rows.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setItems(limit ? rows.slice(0, limit) : rows);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => { load(); }, [load]);

  return { items, loading, reload: load };
}
